/**
 * The nurikabe rules, ported from `backend/apps/puzzletypes/nurikabe/rules.py` (parity is
 * tested against the shared fixtures), plus the live checks only the client runs.
 *
 * Nothing here ever sees the answer key: it reads the public numbers and the player's marks.
 */
import { DOT, SHADED, type Cell, type Clue } from "./types";

export type Issue = "shaded_clue" | "shaded_pool" | "wall_split" | "island_mismatch";
export const ISSUES: Issue[] = ["shaded_clue", "shaded_pool", "wall_split", "island_mismatch"];

export const key = (r: number, c: number): string => `${r},${c}`;
const unkey = (k: string): Cell => k.split(",").map(Number) as Cell;
const byRowMajor = (a: Cell, b: Cell) => a[0] - b[0] || a[1] - b[1];

/** Every numbered cell and its number, keyed `r,c`. */
export function clueCells(clues: Clue[][]): Map<string, number> {
  const found = new Map<string, number>();
  clues.forEach((row, r) =>
    row.forEach((n, c) => {
      if (n !== null && n !== undefined) found.set(key(r, c), n);
    }),
  );
  return found;
}

function neighbours([r, c]: Cell, height: number, width: number): Cell[] {
  const out: Cell[] = [];
  for (const [dr, dc] of [
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
  ]) {
    const rr = r + dr;
    const cc = c + dc;
    if (rr >= 0 && cc >= 0 && rr < height && cc < width) out.push([rr, cc]);
  }
  return out;
}

/** Orthogonally connected groups of `cells`, each sorted, in row-major order. */
export function components(cells: Set<string>, height: number, width: number): Cell[][] {
  const seen = new Set<string>();
  const groups: Cell[][] = [];
  for (const start of [...cells].map(unkey).sort(byRowMajor)) {
    if (seen.has(key(...start))) continue;
    seen.add(key(...start));
    const stack = [start];
    const group: Cell[] = [];
    while (stack.length > 0) {
      const cell = stack.pop() as Cell;
      group.push(cell);
      for (const other of neighbours(cell, height, width)) {
        const k = key(...other);
        if (cells.has(k) && !seen.has(k)) {
          seen.add(k);
          stack.push(other);
        }
      }
    }
    groups.push(group.sort(byRowMajor));
  }
  return groups;
}

function cellsWith(rows: string[], mark: string): Set<string> {
  const found = new Set<string>();
  rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) if (row[c] === mark) found.add(key(r, c));
  });
  return found;
}

/** Top-left corner of every 2×2 block of shaded cells. */
export function pools(shaded: Set<string>): Cell[] {
  return [...shaded]
    .map(unkey)
    .filter(
      ([r, c]) =>
        shaded.has(key(r + 1, c)) && shaded.has(key(r, c + 1)) && shaded.has(key(r + 1, c + 1)),
    )
    .sort(byRowMajor);
}

function allCells(height: number, width: number): Set<string> {
  const all = new Set<string>();
  for (let r = 0; r < height; r++) for (let c = 0; c < width; c++) all.add(key(r, c));
  return all;
}

/** What an answer of the right shape breaks, in the server's order. Empty: solved. */
export function issues(clues: Clue[][], rows: string[]): Issue[] {
  const height = rows.length;
  const width = rows[0]?.length ?? 0;
  const numbers = clueCells(clues);
  const shaded = cellsWith(rows, SHADED);
  const found = new Set<Issue>();
  if ([...numbers.keys()].some((k) => shaded.has(k))) found.add("shaded_clue");
  if (pools(shaded).length > 0) found.add("shaded_pool");
  if (components(shaded, height, width).length > 1) found.add("wall_split");
  const unshaded = new Set([...allCells(height, width)].filter((k) => !shaded.has(k)));
  for (const island of components(unshaded, height, width)) {
    const inside = island.flatMap(([r, c]) => numbers.get(key(r, c)) ?? []);
    if (inside.length !== 1 || inside[0] !== island.length) {
      found.add("island_mismatch");
      break;
    }
  }
  return ISSUES.filter((issue) => found.has(issue));
}

/** Numbered cells with a numbered neighbour below or to the right (one per pair). */
export function adjacentClues(clues: Clue[][]): Cell[] {
  const numbers = clueCells(clues);
  return [...numbers.keys()]
    .map(unkey)
    .filter(([r, c]) => numbers.has(key(r + 1, c)) || numbers.has(key(r, c + 1)))
    .sort(byRowMajor);
}

/** Every cell reachable from `start` through cells of `through`, `start` included. */
function flood(start: Cell[], through: Set<string>, height: number, width: number): Set<string> {
  const reached = new Set(start.map((cell) => key(...cell)));
  const stack = [...start];
  while (stack.length > 0) {
    const cell = stack.pop() as Cell;
    for (const other of neighbours(cell, height, width)) {
      const k = key(...other);
      if (through.has(k) && !reached.has(k)) {
        reached.add(k);
        stack.push(other);
      }
    }
  }
  return reached;
}

export interface Contradictions {
  /** Top-left corner of every 2×2 shaded block. */
  pools: Cell[];
  /** Cells of every island that can no longer be right, row-major. */
  islands: Cell[];
  /** Shaded cells cut off from the rest of the wall, row-major. */
  walls: Cell[];
  /** How many distinct problems: a pool, an island, a piece of wall each count once. */
  count: number;
}

/**
 * What the marks already get wrong, whatever the undecided cells become. Silent when in
 * doubt (CLAUDE.md §9): an island is "sure" only where it is numbered or dotted, and only
 * what no completion can repair is flagged.
 */
export function contradictions(clues: Clue[][], marks: string[]): Contradictions {
  const height = marks.length;
  const width = marks[0]?.length ?? 0;
  const numbers = clueCells(clues);
  const all = allCells(height, width);
  const shaded = new Set([...cellsWith(marks, SHADED)].filter((k) => !numbers.has(k)));
  const dots = cellsWith(marks, DOT);
  const white = new Set([...all].filter((k) => numbers.has(k) || dots.has(k)));
  const open = new Set([...all].filter((k) => !shaded.has(k)));

  const islands: Cell[] = [];
  let wrongIslands = 0;
  for (const island of components(white, height, width)) {
    const inside = island.flatMap(([r, c]) => numbers.get(key(r, c)) ?? []);
    let wrong = inside.length >= 2 || (inside.length === 1 && island.length > inside[0]);
    if (!wrong && inside.length <= 1) {
      const reach = flood(island, open, height, width);
      wrong =
        inside.length === 0
          ? ![...reach].some((k) => numbers.has(k)) // no number can ever claim it
          : reach.size < inside[0]; // walled in before it has its size
    }
    if (wrong) {
      islands.push(...island);
      wrongIslands += 1;
    }
  }

  // The wall may still join through undecided cells; pieces that cannot, can never.
  const potential = new Set([...all].filter((k) => !white.has(k)));
  const pieces = components(potential, height, width)
    .map((piece) => piece.filter(([r, c]) => shaded.has(key(r, c))))
    .filter((piece) => piece.length > 0);
  const main = pieces.reduce<Cell[]>(
    (best, piece) => (piece.length > best.length ? piece : best),
    [],
  );
  const cut = pieces.filter((piece) => piece !== main);
  const blocks = pools(shaded);

  return {
    pools: blocks,
    islands: islands.sort(byRowMajor),
    walls: cut.flat().sort(byRowMajor),
    count: blocks.length + wrongIslands + cut.length,
  };
}

/** Numbered cells whose island is complete: the right size and shaded all round. */
export function satisfiedClues(clues: Clue[][], marks: string[]): Cell[] {
  const height = marks.length;
  const width = marks[0]?.length ?? 0;
  const numbers = clueCells(clues);
  const shaded = cellsWith(marks, SHADED);
  const dots = cellsWith(marks, DOT);
  const white = new Set([...allCells(height, width)].filter((k) => numbers.has(k) || dots.has(k)));
  const done: Cell[] = [];
  for (const island of components(white, height, width)) {
    const inside = island.filter(([r, c]) => numbers.has(key(r, c)));
    if (inside.length !== 1 || numbers.get(key(...inside[0])) !== island.length) continue;
    const members = new Set(island.map((cell) => key(...cell)));
    const sealed = island.every((cell) =>
      neighbours(cell, height, width).every(
        (other) => members.has(key(...other)) || shaded.has(key(...other)),
      ),
    );
    if (sealed) done.push(inside[0]);
  }
  return done.sort(byRowMajor);
}
